import { User } from '../models/user';
import { DatacenterType, UserDatacenterView } from '../models/datacenter-aggrid-view';

const DEFAULT_VIEW_IDS = {
	ECALLOFF: -1,
	ON_VESSEL: -8,
	ON_VESSEL_OVERSEAS_STOCK: -9,
	INVOICE: -2,
	DISPATCH_ADVICE: -3,
	ORDER_ACKNOWLEDGMENT: -4,
	CERTIFICATE: -5,
	XCARB: -6,
	ORDERS_IN_PRODUCTION: -7
};

type ViewColumnState = UserDatacenterView['columnState'];
type ViewSortModel = NonNullable<UserDatacenterView['sortModel']>;
type ViewFilterState = NonNullable<UserDatacenterView['filterState']>;

const COLUMN_STATE_PCALL: ViewColumnState = [
	{ colId: 'batchReference', pinned: 'left' },
	{ colId: 'customerArticleReference', pinned: 'left' },
	{ colId: 'thickness', pinned: 'left' },
	{ colId: 'width', pinned: 'left' },
	{ colId: 'mill' },
	{ colId: 'status' },
	{ colId: 'ecallStatus' },
	{ colId: 'callCreator' },
	{ colId: 'customerReference' },
	{ colId: 'ecallMillBatchReference' },
	{ colId: 'rowCreationDate' },
	{ colId: 'requestedDeliveryDateFrom' },
	{ colId: 'requestedDeliveryDateTo' }
];

const COLUMN_STATE_ON_VESSEL: ViewColumnState = [
	{ colId: 'soldTo', pinned: 'left' },
	{ colId: 'batchReference', pinned: 'left' },
	{ colId: 'thickness', pinned: 'left' },
	{ colId: 'width', pinned: 'left' },
	{ colId: 'length', pinned: 'left' },
	{ colId: 'customerArticleReference', pinned: 'left' },
	{ colId: 'producedLength' },
	{ colId: 'steelgrade' },
	{ colId: 'qtyGross' },
	{ colId: 'transportReference' },
	{ colId: 'status' },
	{ colId: 'customerOrderNumber' },
	{ colId: 'supplierOrderNumber' },
	{ colId: 'loadingProgramNumber' },
	{ colId: 'batchStatusCategory' },
	{ colId: 'currentLocation' },
	{ colId: 'deliveryMode' },
	{ colId: 'statusGroup' },
	{ colId: 'batchTransportMode' },
	{ colId: 'shipmentSectionN-1' },
	{ colId: 'shipmentSectionN-2' },
	{ colId: 'destinationByBoat' },
	{ colId: 'shipTo' },
	{ colId: 'plannedShipmentDate' },
	{ colId: 'callOffUnloadingReference' },
	{ colId: 'mill' },
	{ colId: 'invoiceDate' },
	{ colId: 'supplierInvoiceNumber' },
	{ colId: 'downloadInvoice' },
	{ colId: 'shipmentReferenceDate' },
	{ colId: 'dispatchAdviceDate' },
	{ colId: 'dispatchAdviceNumber' }
];

const COLUMN_STATE_ON_VESSEL_OVERSEAS_STOCK: ViewColumnState = [
	{ colId: 'soldTo', pinned: 'left' },
	{ colId: 'batchReference', pinned: 'left' },
	{ colId: 'thickness', pinned: 'left' },
	{ colId: 'width', pinned: 'left' },
	{ colId: 'length', pinned: 'left' },
	{ colId: 'customerArticleReference', pinned: 'left' },
	{ colId: 'producedLength' },
	{ colId: 'steelgrade' },
	{ colId: 'qtyGross' },
	{ colId: 'transportReference' },
	{ colId: 'status' },
	{ colId: 'customerOrderNumber' },
	{ colId: 'supplierOrderNumber' },
	{ colId: 'batchStatusCategory' },
	{ colId: 'currentLocation' },
	{ colId: 'deliveryMode' },
	{ colId: 'statusGroup' },
	{ colId: 'batchTransportMode' },
	{ colId: 'shipmentReferenceDate' },
	{ colId: 'dispatchAdviceDate' },
	{ colId: 'dispatchAdviceNumber' },
	{ colId: 'shipmentSectionN-1' },
	{ colId: 'shipmentSectionN-2' },
	{ colId: 'destinationByBoat' },
	{ colId: 'loadingProgramNumber' },
	{ colId: 'shipTo' },
	{ colId: 'plannedShipmentDate' },
	{ colId: 'callOffUnloadingReference' },
	{ colId: 'mill' },
	{ colId: 'invoiceDate' },
	{ colId: 'supplierInvoiceNumber' },
	{ colId: 'downloadInvoice' }
];

const COLUMN_STATE_IN: ViewColumnState = [
	{ colId: 'name', pinned: 'left' },
	{ colId: 'subtype', pinned: 'left' },
	{ colId: 'date', pinned: 'left' },
	{ colId: 'invoiceAmount', pinned: 'left' },
	{ colId: 'batchNumber', pinned: 'left' },
	{ colId: 'mill', pinned: 'left' },
	{ colId: 'payer' },
	{ colId: 'supplierOrderNumber' },
	{ colId: 'customerOrderNumber' },
	{ colId: 'dispatchAdviceNumber' },
	{ colId: 'invoicedQuantityValue' },
	{ colId: 'invoiceCurrency' },
	{ colId: 'type' },
	{ colId: 'pdf' }
];

const COLUMN_STATE_DA: ViewColumnState = [
	{ colId: 'name', pinned: 'left' },
	{ colId: 'dispatchAdviceDate', pinned: 'left' },
	{ colId: 'batchNumber', pinned: 'left' },
	{ colId: 'heatNumber', pinned: 'left' },
	{ colId: 'mill', pinned: 'left' },
	{ colId: 'millOrderNumber', pinned: 'left' },
	{ colId: 'shipto' },
	{ colId: 'supplierOrderNumber' },
	{ colId: 'customerOrderNumber' },
	{ colId: 'customerArticleReference' },
	{ colId: 'transportMode' },
	{ colId: 'transportReference' },
	{ colId: 'type' },
	{ colId: 'pdf' }
];

const COLUMN_STATE_OA: ViewColumnState = [
	{ colId: 'name', pinned: 'left' },
	{ colId: 'customer', pinned: 'left' },
	{ colId: 'subtype', pinned: 'left' },
	{ colId: 'orderAcknowledgementDate', pinned: 'left' },
	{ colId: 'supplierOrderNumber', pinned: 'left' },
	{ colId: 'customerOrderNumber', pinned: 'left' },
	{ colId: 'mill' },
	{ colId: 'millOrderNumber' },
	{ colId: 'shipto' },
	{ colId: 'type' },
	{ colId: 'pdf' }
];

const COLUMN_STATE_CE: ViewColumnState = [
	{ colId: 'name', pinned: 'left' },
	{ colId: 'subtype', pinned: 'left' },
	{ colId: 'dispatchAdviceNumber', pinned: 'left' },
	{ colId: 'dispatchAdviceDate', pinned: 'left' },
	{ colId: 'batchNumber', pinned: 'left' },
	{ colId: 'heatNumber', pinned: 'left' },
	{ colId: 'customer' },
	{ colId: 'date' },
	{ colId: 'mill' },
	{ colId: 'shipto' },
	{ colId: 'supplierOrderNumber' },
	{ colId: 'customerOrderNumber' },
	{ colId: 'customerArticleReference' },
	{ colId: 'type' },
	{ colId: 'pdf' }
];

const COLUMN_STATE_XCARB: ViewColumnState = [
	{ colId: 'name', pinned: 'left' },
	{ colId: 'subtype', pinned: 'left' },
	{ colId: 'dispatchAdviceNumber', pinned: 'left' },
	{ colId: 'dispatchAdviceDate', pinned: 'left' },
	{ colId: 'batchNumber', pinned: 'left' },
	{ colId: 'heatNumber', pinned: 'left' },
	{ colId: 'customer' },
	{ colId: 'date' },
	{ colId: 'mill' },
	{ colId: 'shipto' },
	{ colId: 'supplierOrderNumber' },
	{ colId: 'customerOrderNumber' },
	{ colId: 'customerArticleReference' },
	{ colId: 'type' },
	{ colId: 'rrpType' },
	{ colId: 'rrpDocumentLink' },
	{ colId: 'amount' },
	{ colId: 'endQuarter' },
	{ colId: 'pdf' }
];

const SORT_DATE_DESC: ViewSortModel = [{ colId: 'date', sort: 'desc' }];
const SORT_BATCH_DESC: ViewSortModel = [{ colId: 'batchReference', sort: 'desc' }];
const SORT_CDD_ASC: ViewSortModel = [{ colId: 'cddWeek', sort: 'asc' }];

const COLUMN_STATE_ORDERS_IN_PRODUCTION: ViewColumnState = [
	{ colId: 'soldTo', pinned: 'left' },
	{ colId: 'customerOrderNumber', pinned: 'left' },
	{ colId: 'supplierOrderNumber', pinned: 'left' },
	{ colId: 'dimension', pinned: 'left' },
	{ colId: 'steelgrade', pinned: 'left' },
	{ colId: 'mill' },
	{ colId: 'qtyOrdered' },
	{ colId: 'qtyInProduction' },
	{ colId: 'qtyNotDelivered' },
	{ colId: 'qtyDelivered' },
	{ colId: 'cddWeek' },
	{ colId: 'grossLastBatchInStock' }
];

interface DefaultViewFilterTranslations {
	invoice: string;
	dispatchAdvice: string;
	orderAcknowledgment: string;
	certificate: string;
	xcarb: string;
}

function createFilter(code: string, translation: string): ViewFilterState {
	return [
		{
			id: 'type',
			values: [{ code, translation }]
		}
	];
}

export function getDefaultDatacenterViews(
	user: User,
	datacenterType: DatacenterType,
	translations: DefaultViewFilterTranslations
): UserDatacenterView[] {
	const FILTER_IN: ViewFilterState = createFilter('IN', translations.invoice);
	const FILTER_DA: ViewFilterState = createFilter('DA', translations.dispatchAdvice);
	const FILTER_OA: ViewFilterState = createFilter('OA', translations.orderAcknowledgment);
	const FILTER_CE: ViewFilterState = createFilter('CE', translations.certificate);
	const FILTER_XCARB: ViewFilterState = createFilter('XCARB', translations.xcarb);

	const views: UserDatacenterView[] = [];

	if (
		(datacenterType === 'batch')
		&& user.hasService('PCALL')
	) {
		views.push({
			id: DEFAULT_VIEW_IDS.ECALLOFF,
			name: 'Default View: ECallOff',
			isDefault: true,
			type: datacenterType,
			columnState: [...COLUMN_STATE_PCALL],
			sortModel: [...SORT_BATCH_DESC],
			filterState: null
		});
	}

	if (datacenterType === 'batch') {
		views.push({
			id: DEFAULT_VIEW_IDS.ON_VESSEL,
			name: 'On vessel',
			isDefault: true,
			type: datacenterType,
			columnState: [...COLUMN_STATE_ON_VESSEL],
			sortModel: null,
			filterState: [
				{
					id: 'statusCategory',
					values: [{ code: '32', translation: 'On Vessel' }]
				}
			]
		}, {
			id: DEFAULT_VIEW_IDS.ON_VESSEL_OVERSEAS_STOCK,
			name: 'On vessel & overseas stock',
			isDefault: true,
			type: datacenterType,
			columnState: [...COLUMN_STATE_ON_VESSEL_OVERSEAS_STOCK],
			sortModel: null,
			filterState: [
				{
					id: 'statusCategory',
					values: [
						{ code: '32', translation: 'On Vessel' },
						{ code: '33', translation: 'Overseas stock' }
					]
				}
			]
		});
	}

	if (datacenterType === 'document') {
		if (user.hasServiceParameterEqualsTo('PDOCS', 'Invoice', 'True')) {
			views.push({
				id: DEFAULT_VIEW_IDS.INVOICE,
				name: 'Invoice',
				isDefault: true,
				type: 'document',
				columnState: [...COLUMN_STATE_IN],
				sortModel: [...SORT_DATE_DESC],
				filterState: [...FILTER_IN]
			});
		}

		if (user.hasServiceParameterEqualsTo('PDOCS', 'DispatchAdvice', 'True')) {
			views.push({
				id: DEFAULT_VIEW_IDS.DISPATCH_ADVICE,
				name: 'DispatchAdvice',
				isDefault: true,
				type: 'document',
				columnState: [...COLUMN_STATE_DA],
				sortModel: [...SORT_DATE_DESC],
				filterState: [...FILTER_DA]
			});
		}

		if (user.hasServiceParameterEqualsTo('PDOCS', 'OrderAcknowledgment', 'True')) {
			views.push({
				id: DEFAULT_VIEW_IDS.ORDER_ACKNOWLEDGMENT,
				name: 'OrderAcknowledgment',
				isDefault: true,
				type: 'document',
				columnState: [...COLUMN_STATE_OA],
				sortModel: [...SORT_DATE_DESC],
				filterState: [...FILTER_OA]
			});
		}

		if (user.hasServiceParameterEqualsTo('PDOCS', 'Certificate', 'True')) {
			views.push({
				id: DEFAULT_VIEW_IDS.CERTIFICATE,
				name: 'Certificate',
				isDefault: true,
				type: 'document',
				columnState: [...COLUMN_STATE_CE],
				sortModel: [...SORT_DATE_DESC],
				filterState: [...FILTER_CE]
			});
		}

		if (user.hasServiceParameterEqualsTo('PDOCS', 'XCarb', 'True')) {
			views.push({
				id: DEFAULT_VIEW_IDS.XCARB,
				name: 'XCarb',
				isDefault: true,
				type: 'document',
				columnState: [...COLUMN_STATE_XCARB],
				sortModel: [...SORT_DATE_DESC],
				filterState: [...FILTER_XCARB]
			});
		}
	}

	if (datacenterType === 'order') {
		views.push({
			id: DEFAULT_VIEW_IDS.ORDERS_IN_PRODUCTION,
			name: 'Orders in production',
			isDefault: true,
			type: 'order',
			columnState: [...COLUMN_STATE_ORDERS_IN_PRODUCTION],
			sortModel: [...SORT_CDD_ASC],
			filterState: [
				{
					id: 'ordersWithMaterial',
					values: [
						[
							{ apiName: 'grossQtyInProduction', selected: true },
							{ apiName: 'grossQtyAvailableReadyToShip', selected: true },
							{ apiName: 'grossQtyAvailableInTransit', selected: true },
							{ apiName: 'grossQtyAvailableInConsignment', selected: false },
							{ apiName: 'grossQtyDelivered', selected: false }
						]
					]
				},
				{
					id: 'orderItemStatus',
					values: [
						{ code: '01', translation: 'Open' },
						{ code: '05', translation: 'Completely Produced' }
					]
				}
			]
		});
	}

	return views;
}
